import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  DEFAULT_STAFF_MANUAL_APPLICATION_FORM_DATA,
  STAFF_MANUAL_APPLICATION_FORM_STORAGE_KEY,
  STAFF_MANUAL_APPLICATION_MAX_STEP_INDEX,
} from '@fingerprinting/features/staff/application/create/constants'
import type { StaffManualApplicationFormData } from '@fingerprinting/features/staff/application/create/types'

interface StaffManualApplicationFormStoreState {
  currentStepIndex: number
  highestStepReached: number
  formData: StaffManualApplicationFormData
  isSubmitting: boolean

  setCurrentStepIndex: (step: number) => void
  updateFormData: <K extends keyof StaffManualApplicationFormData>(
    key: K,
    value: StaffManualApplicationFormData[K]
  ) => void
  setFormData: (
    data:
      | Partial<StaffManualApplicationFormData>
      | ((
          prev: StaffManualApplicationFormData
        ) => Partial<StaffManualApplicationFormData>)
  ) => void
  resetForm: () => void
  setSubmitting: (isSubmitting: boolean) => void
}

export const useStaffManualApplicationFormStore = create<StaffManualApplicationFormStoreState>()(
  persist(
    (set) => ({
      currentStepIndex: 0,
      highestStepReached: 0,
      formData: DEFAULT_STAFF_MANUAL_APPLICATION_FORM_DATA,
      isSubmitting: false,

      setCurrentStepIndex: (step: number) => {
        if (step < 0 || step > STAFF_MANUAL_APPLICATION_MAX_STEP_INDEX) return
        set((s) => ({
          currentStepIndex: step,
          highestStepReached: Math.max(s.highestStepReached, step),
        }))
      },

      updateFormData: <K extends keyof StaffManualApplicationFormData>(
        key: K,
        value: StaffManualApplicationFormData[K]
      ) => {
        set((state) => ({
          formData: {
            ...state.formData,
            [key]: value,
          },
        }))
      },

      setFormData: (
        data:
          | Partial<StaffManualApplicationFormData>
          | ((
              prev: StaffManualApplicationFormData
            ) => Partial<StaffManualApplicationFormData>)
      ) => {
        set((state) => {
          const patch = typeof data === 'function' ? data(state.formData) : data
          return {
            formData: {
              ...state.formData,
              ...patch,
            },
          }
        })
      },

      resetForm: () => {
        set({
          currentStepIndex: 0,
          highestStepReached: 0,
          formData: DEFAULT_STAFF_MANUAL_APPLICATION_FORM_DATA,
          isSubmitting: false,
        })
      },

      setSubmitting: (isSubmitting: boolean) => {
        set({ isSubmitting })
      },
    }),
    {
      name: STAFF_MANUAL_APPLICATION_FORM_STORAGE_KEY,
      partialize: (state) => ({
        currentStepIndex: state.currentStepIndex,
        highestStepReached: state.highestStepReached,
        formData: state.formData,
      }),
    }
  )
)
